/**
 * VantageContext Store
 *
 * Manages the per-view state of the Vantage context panel: which extensions are pinned open and whether the history
 * timeline is expanded.
 */

import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';

type VantageContextCategoryFilters = {
  kind: string[];
  extension: string[];
  seeqUser: string[];
};

export interface VantageContextState {
  /** Names of the extensions the user pinned so they open automatically when the view loads. */
  pinnedExtensions: string[];
  /** Whether the history timeline in the context panel is expanded. */
  historyOpen: boolean;
  /** Not set by the UI; kept only so saved worksteps keep their shape. */
  contextSearchFilterText: string;
  /** Not set by the UI; kept only so saved worksteps keep their shape. */
  contextCategoryFilters: VantageContextCategoryFilters;
}

export type VantageContextDehydratedState = VantageContextState;

type VantageContextActions = {
  /** Replaces the full list of pinned extension names. */
  setPinnedExtensions: (pinnedExtensions: string[]) => void;
  /** Expands or collapses the history timeline in the context panel. */
  setHistoryOpen: (isOpen: boolean) => void;
  /** Restores the store to its initial state. */
  reset: ResettableStoreState<VantageContextState>;
};

const initialState: VantageContextState = Object.freeze({
  pinnedExtensions: [],
  historyOpen: true,
  contextSearchFilterText: '',
  contextCategoryFilters: {
    kind: [],
    extension: [],
    seeqUser: [],
  },
});

export const dehydrateVantageContextState = (state: VantageContextState): VantageContextDehydratedState => ({
  pinnedExtensions: state.pinnedExtensions,
  historyOpen: state.historyOpen,
  contextSearchFilterText: state.contextSearchFilterText,
  contextCategoryFilters: state.contextCategoryFilters,
});

const vantageContextActionsFactory: ActionsFactory<VantageContextState, VantageContextActions> = (
  set,
  _get,
  resetState,
) => ({
  setPinnedExtensions: (pinnedExtensions) =>
    set((draft) => {
      draft.pinnedExtensions = pinnedExtensions;
    }),
  setHistoryOpen: (isOpen) =>
    set((draft) => {
      draft.historyOpen = isOpen;
    }),
  reset: resetState,
});

const [VantageContextStoreProvider, useVantageContextStoreInstance, useVantageContextSelector] = createStore(
  'VantageContext',
  initialState,
  vantageContextActionsFactory,
);

export { VantageContextStoreProvider };

export const usePinnedExtensions = () => useVantageContextSelector((state) => state.pinnedExtensions);
export const useHistoryOpen = () => useVantageContextSelector((state) => state.historyOpen);
export const useVantageContextActions = () => useVantageContextStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestVantageContextStore(overrides?: Partial<VantageContextState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestVantageContextStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, vantageContextActionsFactory);
}

/**
 * Registers the VantageContext store with the persistence system.
 * Uses the legacy store name 'sqVantageContextStore' for workstep compatibility.
 */
export const usePersistVantageContextStore = () => {
  const register = useRegisterPersistedStore();
  const store = useVantageContextStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqVantageContextStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateVantageContextState,
      }),
    [register, store],
  );
};
